import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { contractorPageSizeOptions } from 'src/constants/contractor/contractorGrid'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getSelectedMarket } from 'src/selectors/market'
import { handleApiError } from 'src/utils/errorHandlers'
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  ISubcontractorRequestsTableContext,
  SubcontractorRequestsTableContext,
} from './SubcontractorRequestsTable.context'
import { EmptySubcontractorRequests } from './EmptySubcontractorRequests'
import { SubcontractorRequestsHeader } from './header/SubcontractorRequestsHeader'
import {
  subcontractorRequestsColumns,
  subcontractorRequestsPageSizeOptions,
} from 'src/constants/subcontractorRequests/subcontractorRequestsGrid'
import { SubcontractorRequestsActionMenu } from './SubcontractorRequestsActionMenu'
import { SubcontractorRequest } from 'src/interfaces/subcontractor'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchSubcontractor, FetchSubcontractorRequests } from 'src/constants/actionTypes'
import { Pagination } from 'src/interfaces/table'
import { ContractorDetails } from 'src/interfaces/contractor'
import { ContractorDetailsModal } from 'src/components/contractor-details/ContractorDetailsModal'

interface FetchSubcontractorRequestsParams {
  limit?: number
  offset?: number
  searchQuery: string
  masterContractorFilter: Array<string>
  sortModel: GridSortModel
}

const getRowId = (row: SubcontractorRequest) => row.id

const SubcontractorRequestsTableComponent = () => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(contractorPageSizeOptions)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [subcontractorRequests, setSubcontractorRequests] = useState<Array<SubcontractorRequest>>(
    [],
  )
  const [selectedSubcontractorRequestIds, setSelectedSubcontractorRequestIds] =
    useState<GridRowSelectionModel>([])
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [masterContractorFilter, setMasterContractorFilter] = useState<Array<string>>([])

  const [subcontractorRequestToAction, setSubcontractorRequestToAction] =
    useState<SubcontractorRequest | null>(null)
  const [totalSubcontractorRequests, setTotalSubcontractorRequests] = useState<number>(0)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'firstName',
      sort: 'desc',
    },
  ])
  const [selectedSubcontractor, setSelectedSubcontractor] = useState<SubcontractorRequest | null>(
    null,
  )
  const [subcontractorDetails, setSubcontractorDetails] = useState<ContractorDetails | null>(null)

  const fetchSubcontractorRequests = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      sortModel,
      masterContractorFilter,
    }: FetchSubcontractorRequestsParams) => {
      setSelectedSubcontractorRequestIds([])

      setIsLoading(true)

      try {
        const params: any = {
          limit,
          offset,
        }

        if (searchQuery) {
          params.contractorSearch = searchQuery
        }

        if (masterContractorFilter?.length) {
          params.masterContractorIds = masterContractorFilter
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0]?.field}:${sortModel[0]?.sort?.toUpperCase()}`
        }

        const payload: { data: Array<SubcontractorRequest>; pagination: Pagination } =
          await createAsyncAction(dispatch, FetchSubcontractorRequests.request(params))

        setSubcontractorRequests(payload.data)
        setTotalSubcontractorRequests(payload.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    // TODO: change later
    [selectedMarket?.id],
  )

  const handleRowSelectionChange = useCallback((newSelectionModel: GridRowSelectionModel) => {
    setSelectedSubcontractorRequestIds(newSelectionModel)
  }, [])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const openSubcontractorRequestItemAction = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      subcontractorRequestToAction: SubcontractorRequest,
    ) => {
      setActionAnchor(event.currentTarget)
      setSubcontractorRequestToAction(subcontractorRequestToAction)
    },
    [],
  )

  const closeSubcontractorRequestItemAction = useCallback(() => {
    setActionAnchor(null)
  }, [])

  const clearSubcontractorRequestToAction = useCallback(() => {
    setSubcontractorRequestToAction(null)
  }, [])

  const subcontactorRequestsContextValue = useMemo<ISubcontractorRequestsTableContext>(() => {
    return {
      searchQuery,
      setSearchQuery,

      masterContractorFilter,
      setMasterContractorFilter,

      actionAnchor,
      subcontractorRequestToAction,
      openSubcontractorRequestItemAction,
      closeSubcontractorRequestItemAction,
      clearSubcontractorRequestToAction,

      selectedSubcontractorRequestIds,
    }
  }, [
    searchQuery,
    setSearchQuery,

    masterContractorFilter,
    setMasterContractorFilter,

    actionAnchor,
    subcontractorRequestToAction,
    openSubcontractorRequestItemAction,
    closeSubcontractorRequestItemAction,
    clearSubcontractorRequestToAction,

    selectedSubcontractorRequestIds,
  ])

  const fetchContractorDetails = useCallback(async (subcontractor: SubcontractorRequest) => {
    try {
      const newSubcontractorDetails: ContractorDetails = await createAsyncAction(
        dispatch,
        FetchSubcontractor.request({
          partnerDriverId: subcontractor.partnerDriverId,
          contractorState: 'active',
        }),
      )
      setSubcontractorDetails(newSubcontractorDetails)
    } catch (err) {
      handleApiError(err)
    }
  }, [])

  const handleRowClick = useCallback(({ row }: { row: SubcontractorRequest }) => {
    setSelectedSubcontractor(row)
  }, [])

  useEffect(() => {
    if (!selectedSubcontractor) {
      setSubcontractorDetails(null)
    } else {
      fetchContractorDetails(selectedSubcontractor)
    }
  }, [selectedSubcontractor?.id])

  useEffect(() => {
    fetchSubcontractorRequests({
      limit,
      offset,
      searchQuery,
      sortModel,
      masterContractorFilter,
    })
  }, [limit, offset, searchQuery, sortModel, masterContractorFilter, selectedMarket?.id])

  const handleCloseDetails = useCallback(() => {
    setSelectedSubcontractor(null)
    setSubcontractorDetails(null)
  }, [])

  return (
    <SubcontractorRequestsTableContext.Provider value={subcontactorRequestsContextValue}>
      <SubcontractorRequestsHeader />
      <GridTable<SubcontractorRequest>
        rows={subcontractorRequests}
        isLoading={isLoading}
        columns={subcontractorRequestsColumns}
        paginationModel={paginationModel}
        pageSizeOptions={subcontractorRequestsPageSizeOptions}
        rowCount={totalSubcontractorRequests}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handleChangePagination}
        getRowId={getRowId}
        EmptyElement={EmptySubcontractorRequests}
        onRowSelectionModelChange={handleRowSelectionChange}
        rowSelectionModel={selectedSubcontractorRequestIds}
        checkboxSelection
        onRowClick={handleRowClick}
      />
      <SubcontractorRequestsActionMenu />
      {subcontractorDetails && (
        <ContractorDetailsModal
          key={subcontractorDetails.id}
          contractorDetails={subcontractorDetails}
          flow="contractors"
          onClose={handleCloseDetails}
          onRefresh={handleCloseDetails}
          isSubcontractor
          isRequestApprove
        />
      )}
    </SubcontractorRequestsTableContext.Provider>
  )
}

export const SubcontractorRequestsTable = memo(SubcontractorRequestsTableComponent)
